import { ChangeDetectionStrategy, Component, input, output, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatListModule, MatListOption } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { connectState, RxAfterViewInit } from '@examdojo/angular/util';
import { InternationalizationService } from '@examdojo/core/i18n';
import { Icon, IconComponent } from '@examdojo/core/icon';
import { MatTooltipTruncateDirective } from '@examdojo/core/mat-tooltip-truncate';
import { SelectOption } from '@examdojo/util/select-option';
import { combineLatest, delay, filter, map, Observable, of, startWith, switchMap, tap } from 'rxjs';
import { TopicCodeNumberComponent } from '../topic-code-number/topic-code-number.component';

export interface SelectionListOption<T> extends SelectOption<T> {
  figure?: { type: 'icon'; icon: Icon } | { type: 'label'; label: string };
  labelAdditional?: string;
  cornerTag?: string;
}

export type CompareWithFn<T> = (o1: T, o2: T) => boolean;

@RxAfterViewInit()
@Component({
  selector: 'dojo-selection-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatListModule,
    ReactiveFormsModule,
    IconComponent,
    TopicCodeNumberComponent,
    MatTooltip,
    MatTooltipTruncateDirective,
  ],
  host: { class: 'block' },
  template: `
    <mat-selection-list
      class="custom-selection-list-checkbox"
      [formControl]="formCtrl()"
      (selectionChange)="optionSelected.emit($event.options)"
      [multiple]="multiple()"
      [compareWith]="compareWith() ?? defaultCompareWith"
    >
      @for (option of state.options; track option.value; let idx = $index) {
        <mat-list-option
          [value]="option.value"
          [class.hide-indicator]="hideIndicator()"
          [disabled]="option.disabled === true"
        >
          <div class="flex items-center gap-2">
            @if (option.figure) {
              @switch (option.figure.type) {
                @case ('icon') {
                  <dojo-icon [icon]="option.figure.icon" [alt]="option.label" [size]="figureIconSize()" />
                }
                @case ('label') {
                  <dojo-topic-code-number [codeNumber]="option.figure.label" />
                }
              }
            }

            @if (option?.labelDescription) {
              <div class="inner-component ml-1 flex flex-col gap-1 truncate">
                <span class="text-primary truncate" [matTooltip]="option.label">
                  {{ option.label }}
                </span>
                <span class="text-secondary truncate font-normal" [matTooltip]="option.labelDescription">
                  {{ option.labelDescription }}
                </span>
              </div>
            } @else {
              <span class="w-full truncate" [matTooltip]="option.combinedLabelTooltip">
                @if (labelsAreTranslationKeys()) {
                  @if (isDividedLabel()) {
                    <span class="flex w-full items-center justify-between gap-2">
                      <span>{{ option.label }}</span>
                      <span class="text-xs font-semibold">{{ option.labelAdditional }}</span>
                    </span>
                  } @else {
                    {{ option.label }}
                  }
                } @else {
                  @if (isDividedLabel()) {
                    <span class="flex w-full items-center justify-between gap-2">
                      <span>{{ option.label }}</span>
                      <span class="text-xs font-semibold">{{ option.labelAdditional }}</span>
                    </span>
                  } @else {
                    {{ option.label }}
                  }
                }
              </span>
            }
          </div>
        </mat-list-option>
      }
    </mat-selection-list>
  `,
})
export class SelectionListComponent<T = unknown> {
  constructor(private readonly internationalizationService: InternationalizationService) {
    this.setInitialFocus().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly formCtrl = input.required<FormControl>();

  readonly options = input<Array<SelectionListOption<T>>>([]);

  readonly multiple = input(false);
  readonly labelsAreTranslationKeys = input(false);
  readonly hasInitialFocus = input(false);
  readonly isDividedLabel = input(false);
  readonly hideIndicator = input(false);
  readonly figureIconSize = input(30);
  readonly compareWith = input<CompareWithFn<T> | undefined>(undefined);

  readonly options$ = combineLatest([
    toObservable(this.options),
    toObservable(this.isDividedLabel),
    toObservable(this.labelsAreTranslationKeys),
  ]).pipe(
    switchMap(([options, isDividedLabel, labelsAreTranslationKeys]) =>
      combineLatest(
        options.map((option) =>
          combineLatest({
            label: labelsAreTranslationKeys
              ? this.internationalizationService.selectTranslate(option.label, option.translationParams || {})
              : of(option.label),
            labelAdditional: labelsAreTranslationKeys
              ? option.labelAdditional
                ? this.internationalizationService.selectTranslate(
                    option.labelAdditional,
                    option.translationParams || {},
                  )
                : of('')
              : of(option.labelAdditional || ''),
            labelDescription: option.labelDescription
              ? labelsAreTranslationKeys
                ? this.internationalizationService.selectTranslate(
                    option.labelDescription,
                    option.translationParams || {},
                  )
                : of(option.labelDescription)
              : of(''),
          }).pipe(
            map(({ label, labelAdditional, labelDescription }) => ({
              ...option,
              label,
              labelAdditional,
              combinedLabelTooltip: isDividedLabel ? `${label} — ${labelAdditional}` : label,
              labelDescription,
            })),
          ),
        ),
      ),
    ),
    startWith([]),
  );

  readonly optionSelected = output<MatListOption[]>();

  private readonly matListOption = viewChild<MatListOption>(MatListOption);

  readonly ngAfterViewInit$!: Observable<void>;

  readonly state = connectState({
    options: this.options$,
  });

  readonly defaultCompareWith: CompareWithFn<T> = (o1, o2) => o1 === o2;

  setFocus() {
    this.matListOption()?.focus();
  }

  private setInitialFocus() {
    return this.ngAfterViewInit$.pipe(
      filter(() => this.hasInitialFocus()),
      delay(0),
      tap(() => this.setFocus()),
    );
  }
}
